import type { Dict } from '../types'

/**
 * Norwegian Bokmål. Unlike sv/fr/nl there is NO existing MOS MOSH copy in Core to translate from
 * (`brandLocalization.tradein2` has no `nb` entries), so this is modelled on the Danish dictionary —
 * the nearest language — with Norwegian vocabulary: innbytte, verdibevis, fraktetikett,
 * innleveringssted, nettbutikk. Needs a native proofread before it goes in front of customers.
 */
export const nb: Dict = {
  languageName: 'Norsk',

  common: {
    points: 'poeng',
  },

  // Subject-word lexicon (see en.ts). Norwegian defaults for clothing (klær = plural), so the copy
  // agrees in the plural. A brand supplies the full phrase per form, so its own gender and number
  // travel with the word.
  //
  // verdibevis is neuter (et verdibevis), like Danish værdibevis — the strings that carry their own
  // determiner ("ditt", "et", "klart") are flagged where they appear.
  subject: {
    noun: 'klær',
    nounPoss: 'dine klær',
    nounDef: 'klærne',
    reward: 'verdibevis',
    rewardPoss: 'ditt verdibevis',
    rewardDef: 'verdibeviset',
    rewardPlural: 'verdibevis',
  },

  auth: {
    signingIn: 'Logger deg inn…',
    stillWorking: 'Jobber fortsatt — vi henter de tidligere bestillingene dine. Det kan ta et øyeblikk.',
    loadingItems: 'Henter varene dine…',
    errorTitle: 'Vi kunne ikke starte innbyttet ditt',
    errorGeneric: 'Noe gikk galt under innloggingen.',
    missingShop: 'Appen er feilkonfigurert — shopId mangler.',
    needSignIn: 'Du må logge inn før du kan bytte inn.',
    signInFailed: 'Innloggingen mislyktes. Prøv igjen.',
    linkInvalid: 'Denne innloggingslenken virker ikke lenger. Profilen den tilhører kan ha blitt slettet. Gå tilbake til forsiden for å logge inn igjen.',
    backToFrontPage: 'Tilbake til forsiden',
  },

  config: {
    title: 'Konfigurasjonsfeil',
    missingShop: 'Dette innbyttet mangler butikkonfigurasjonen sin (shopId). Kontakt butikken.',
  },

  login: {
    title: 'Gi {nounPoss} nytt liv',
    subtitle: 'Logg inn for å starte innbyttet ditt og gjør plagg du ikke lenger bruker om til et {reward}.',
    emailLabel: 'E-post',
    emailPlaceholder: 'deg@eksempel.no',
    start: 'Fortsett',
    working: 'Vent litt…',
    emailSentTitle: 'Sjekk e-posten din',
    emailSentBody: 'Vi har sendt en innloggingslenke til {email}. Åpne den på denne enheten for å fortsette.',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '123 45 678',
    sendCode: 'Send kode',
    codeLabel: 'Bekreftelseskode',
    codePlaceholder: '6-sifret kode',
    verify: 'Bekreft og fortsett',
    changePhone: 'Bruk et annet nummer',
    back: 'Tilbake',
    methodSms: 'Logg inn med SMS',
    methodSmsDesc: 'Vi sender deg en kode på sms — hvis vi har telefonnummeret ditt registrert.',
    methodEmail: 'Logg inn med e-post',
    methodEmailDesc: 'Vi sender en innloggingslenke til innboksen din.',
    or: 'eller',
    wholesale: 'Kjøpt i butikk eller fått i gave?',
    emailInvalid: 'Skriv inn en gyldig e-postadresse.',
    phoneInvalid: 'Skriv inn et gyldig telefonnummer.',
    codeInvalid: 'Koden fungerte ikke. Prøv igjen.',
    failed: 'Noe gikk galt. Prøv igjen.',
    step1Title: 'Finn plaggene dine',
    step1Body: 'Søk eller ta et bilde av det du ikke lenger bruker.',
    step2Title: 'Send dem inn',
    step2Body: 'Pakk alt i én pakke med den forhåndsbetalte fraktetiketten din.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'Få {rewardPoss} når vi har mottatt varene dine.',
      handin: 'Få {rewardPoss} så snart pakken din er skannet på innleveringsstedet.',
      instant: 'Få {rewardPoss} med en gang — send deretter varene dine til oss.',
    },
  },

  country: {
    title: 'Landet ditt støttes kanskje ikke',
    body: 'Det ser ut til at vi kanskje ikke har en fraktløsning som kan sende et innbytte fra landet ditt til lageret vårt. Du er velkommen til å fortsette, men vi får kanskje ikke fullført innhentingen.',
    dismiss: 'Fortsett likevel',
  },

  menu: {
    open: 'Meny',
    language: 'Språk',
    myTradeIns: 'Mine innbytter',
    howItWorks: 'Slik fungerer det',
    noneYet: 'Ingen ennå',
    logout: 'Logg ut',
    soon: 'Snart',
  },

  // Verdibevisrader, gjenbrukt av siden «Mine innbytter» til verdibevisseksjonen.
  rewards: {
    activeHeading: 'Tilgjengelige',
    spentHeading: 'Brukte',
    expires: 'Utløper {date}',
    active: 'Aktivt',
    spentBadge: 'Brukt',
  },
  // Kontosiden «Mine innbytter»: verdibevis først, deretter innbyttehistorikk + detaljdialog.
  myTradeIns: {
    title: 'Mine innbytter',
    subtitle: 'Innbyttene dine og {rewardPlural}.',
    back: 'Tilbake til innbyttet',
    loading: 'Henter innbyttene dine…',
    empty: 'Du har ingen innbytter eller {rewardPlural} ennå.',
    historyHeading: 'Innbyttene dine',
    tradeInPrefix: 'Innbytte',
    itemsCount: '{count} varer',
    statusReceived: 'Mottatt',
    statusOnItsWay: 'På vei til oss',
    statusAwaiting: 'Venter på pakken din',
    detailTitle: 'Innbyttedetaljer',
    nextHeading: 'Hva skjer nå',
    nextReceived: 'Vi har mottatt varene dine — {rewardPoss} er på vei.',
    nextTransit:
      'Pakken din er på vei til oss. Vi sender deg en e-post så snart vi har mottatt og sjekket varene dine.',
    nextPickup: 'Ta med varene dine til butikken vår, så ordner vi resten.',
    nextShip: 'Pakk varene dine og send dem til oss med opplysningene nedenfor.',
    itemsHeading: 'Varer i dette innbyttet',
    labelUnavailable: 'Fraktetiketten er ikke lenger tilgjengelig.',
    close: 'Lukk',
  },

  steps: {
    items: 'Varer',
    story: 'Historien din',
    details: 'Opplysninger',
    shipping: 'Frakt',
    confirm: 'Bekreft',
  },

  rail: {
    title: 'Innbyttet ditt',
    itemCount: '{count} vare',
    itemCountPlural: '{count} varer',
    empty: 'Ingen varer ennå — legg til den første, og se verdien vokse.',
    loading: 'Henter innbyttet ditt…',
    estimatedReward: 'Samlet verdi',
    rewardNoteConfirmed: {
      received: 'Bekreftes når vi mottar varene dine',
      handin: 'Bekreftes når du leverer inn pakken din',
      instant: 'Utstedes så snart du bekrefter innbyttet ditt',
    },
    rewardNoteAdd: 'Legg til varer for å se verdien',
    addedManually: 'Lagt til manuelt',
    maxExceeded: 'Dette innbyttet overstiger maksimum på {amount}. Fjern en vare for å fortsette.',
    remove: 'Fjern {name}',
    back: 'Tilbake',
    continue: 'Fortsett',
    confirmTradeIn: 'Bekreft innbytte',
    done: 'Ferdig',
  },

  items: {
    firstTitle: 'La oss finne den første varen din',
    firstSubtitle: 'Ta et bilde, eller søk i katalogen.',
    anotherTitle: 'Legge til enda en vare?',
    anotherSubtitle: 'Gå gjennom alt før du fortsetter.',
    selectTitle: 'Velg varer til innbytte',
    selectSubtitle: 'Fra tidligere bestillinger — trykk for å legge til.',
    manualTitle: 'Legg til en vare manuelt',
    manualSubtitle: 'For varer kjøpt andre steder.',
    estReward: 'Verdi',
    bought: 'Kjøpt {date}',
    addManually: 'Legg til manuelt',
    boughtElsewhere: 'Kjøpt et annet sted?',
    addedCount: '{count} lagt til',
    nothingTitle: 'Ingenting å bytte inn',
    nothingBody:
      'Vi fant ingen varer på kontoen din som kan byttes inn. Innbytte her gjelder bare varer kjøpt hos oss — så snart du har en gyldig bestilling, vises den her.',
  },

  intro: {
    title: 'Slik fungerer innbytte',
    lead: 'Bytt inn {noun} fra {brand} som du ikke lenger bruker — og få {rewardPoss} til nytt eller brukt.',
    step1Title: 'Finn plaggene dine',
    step1Body: 'Ta et bilde av det du ikke lenger bruker, eller søk i katalogen vår. Legg til så mange plagg du vil.',
    step2Title: 'Send dem inn',
    step2Body: 'Pakk alt i én pakke med den forhåndsbetalte fraktetiketten din — frakten spanderer vi.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'Når vi har mottatt og sjekket varene dine, sender vi {rewardPoss} på e-post.',
      handin: 'Så snart pakken din skannes ved innlevering, sender vi {rewardPoss} på e-post.',
      instant: '{rewardPoss|cap} sendes på e-post så snart du bekrefter — deretter sender du varene dine til oss.',
    },
    cta: 'Forstått',
    close: 'Lukk',
  },

  finder: {
    search: 'Søk i katalogen',
    photo: 'Legg til med bilde',
    searchTitle: 'Søk i katalogen',
    searchSub: 'På SKU, EAN eller produktnavn.',
    searchPlaceholder: 'Søk på SKU, EAN eller produktnavn',
    carelabelHint: 'Tips: du finner SKU- eller stilnummeret på vaskelappen i varen.',
    searching: 'Søker…',
    searchFailed: 'Søket mislyktes — prøv igjen.',
    noMatches: 'Ingen treff på «{query}».',
    unavailable: 'Katalogsøk er ikke tilgjengelig for denne butikken.',
    estReward: 'Verdi',
    notEligible: 'Kan ikke byttes inn',
    add: 'Legg til',
    adding: 'Legger til…',
    added: '{name} lagt til · +{amount}',
    backToOrders: 'Tilbake til bestillingene dine',
    showMore: 'Vis flere resultater',
    loadingMore: 'Henter…',
  },

  photo: {
    dropTitle: 'Ta et bilde av varen din',
    dropHint: 'Så finner vi lignende varer i katalogen vår.',
    or: 'eller',
    wayDevice: 'Ta eller last opp et bilde',
    wayPhone: 'Bruk kameraet på telefonen din',
    qrAgainTitle: 'Prøv et annet bilde',
    qrAgainHint: 'Ta et nytt bilde med telefonen, så søker vi automatisk igjen.',
    analysingTitle: 'Finner de beste treffene dine…',
    analysingHint: 'Sammenligner bildet ditt med katalogen vår',
    uploadingTitle: 'Laster opp bildet ditt…',
    uploadingHint: 'Et øyeblikk',
    yourPhoto: 'Bildet ditt',
    matchedTo: 'Matchet mot katalogen vår',
    replace: 'Erstatt',
    headingConfident: 'Ligner en av disse på varen din?',
    headingCategory: 'Nærmeste treff i {category}',
    noMatchHeading: 'Vi fant ingen nære treff',
    noMatchBody:
      'Ingen av katalogvarene våre var et nært treff. Prøv et annet bilde, eller søk på navn i stedet.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Ikke søkt ennå',
    searchFailedHeading: 'Vi kunne ikke søke akkurat nå',
    searchFailedBody:
      'Noe gikk galt hos oss, så vi har ikke sammenlignet bildet ditt ennå. Bildet ditt er lagret — prøv igjen.',
    tryAgain: 'Prøv igjen',
    refine: 'Filtrer etter kategori',
    clear: 'Tøm',
    noneOfThese: 'Ingen av disse',
    // «Legg til etter kategori» — reserven fra plassholderfeltet i bilderesultatene.
    categoryCardTitle: 'Finner du ikke varen din?',
    categoryCardHint: 'Velg en kategori i stedet',
    categoryPickTitle: 'Hva slags vare er det?',
    categoryPickHint: 'Vi bruker bildet du nettopp tok.',
    categoryValue: 'Verdi',
    categoryConfirm: 'Legg til i innbyttet',
    categoryBack: 'Tilbake',
    uploadAnother: 'Last opp et annet bilde',
    bestMatch: 'Beste treff',
    uploadFailed: 'Noe gikk galt — prøv igjen.',
  },

  match: {
    strong: 'Sterkt treff',
    colour: 'Farge: {color}',
    estimatedReward: 'Verdi',
    backToMatches: 'Tilbake til treffene',
    backToResults: 'Tilbake til resultatene',
    confirm: 'Ja, det er varen min',
  },

  details: {
    title: 'Opplysningene dine',
    subtitle: 'Slik at vi kan lage den forhåndsbetalte fraktetiketten din og sende {rewardPoss}.',
    contact: 'Kontakt',
    address: 'Adresse',
    fullName: 'Fullt navn',
    email: 'E-post',
    country: 'Land',
    streetAddress: 'Gateadresse',
    postalCode: 'Postnummer',
    city: 'Sted',
    prefix: 'Landkode',
    phone: 'Telefon',
    emailLocked: 'E-postadressen din er knyttet til kontoen din og kan ikke endres her.',
    consentPre: 'Jeg godtar ',
    consentLink: 'vilkårene for innbytte',
    consentPost: ' og bekrefter at varene er mine å bytte inn.',
    required: 'Dette feltet er påkrevd.',
    emailInvalid: 'Skriv inn en gyldig e-postadresse.',
    phoneInvalid: 'Skriv inn et gyldig telefonnummer.',
    zipInvalid: 'Skriv inn et gyldig postnummer',
    zipFormat: 'format: {format}',
    loading: 'Henter opplysningene dine…',
    saveFailed: 'Vi kunne ikke lagre opplysningene dine. Prøv igjen.',
  },

  terms: {
    title: 'Vilkår og betingelser for innbytte av {noun}',
    // {brand} er butikkens merkenavn (config.brandName), eller en generisk reserve hvis det mangler.
    introLead:
      'Som en del av secondhand-initiativet vårt kan du bytte inn {nounPoss} fra {brand} mot et {reward}.',
    introAccept:
      'Ved å sende inn {nounPoss} fra {brand} godtar du at det bare utstedes {reward} for {noun} som oppfyller vilkårene nedenfor, og at {noun} som ikke oppfyller kravene IKKE sendes i retur til deg.',
    introDonate:
      '{noun|cap} som ikke oppfyller kravene våre, doneres til veldedighet eller gis til virksomheter som kan gjenbruke varene eller materialene i dem.',
    requirementsTitle: 'Krav til {nounDef} du leverer inn',
    requirementsBody:
      'Vi tar imot {noun} fra {brand} som er rene, og der alle originale merkelapper er intakte — både nakkelappen og vaskelappen (vaskeanvisning m.m.).',
    howToTitle: 'Slik leverer du inn {nounPoss}',
    howToRegister:
      'Du registrerer {brand}-produktene du ønsker å levere inn. Under registreringen ser du verdien av hvert produkt — beløpet du får som {reward} i bytte mot {nounPoss}.',
    howToAdjust:
      'Hvis det vi mottar ikke stemmer med det du registrerte, retter vi til riktig produkt og justerer den samlede verdien din tilsvarende. Ved å sende inn {nounPoss} godtar du at vi gjør denne justeringen.',
    howToLabel:
      'Når du har fullført registreringen, får du en fraktetikett eller QR-kode for å sende inn {nounPoss}. Denne fraktetiketten er forhåndsbetalt av {brand}.',
    howToFinal:
      'Når du har levert inn {nounPoss}, kan du ikke angre innsendingen, og vi sender ikke produktene dine i retur.',
    voucherTitle: '{reward|cap}',
    voucherIssue: {
      received:
        'Du får et {reward} når vi har bekreftet at {nounPoss} oppfyller kravene ovenfor. {rewardDef|cap} sendes til e-postadressen du oppga.',
      handin:
        'Du får et {reward} når pakken din er registrert på innleveringsstedet. {rewardDef|cap} sendes til e-postadressen du oppga. Hvis {nounPoss} ikke oppfyller kravene ovenfor, kan {rewardDef} trekkes tilbake.',
      instant:
        'Du får et {reward} så snart du registrerer innbyttet ditt. {rewardDef|cap} sendes til e-postadressen du oppga. Hvis {nounPoss} ikke sendes til oss, eller ikke oppfyller kravene ovenfor, kan {rewardDef} trekkes tilbake.',
    },
    voucherValidity:
      '{rewardDef|cap} er gyldig i 3 år fra utstedelsen og kan brukes til kjøp hos {brand} på nett. {rewardDef|cap} kan ikke løses inn i kontanter.',
    closing: 'Vi gleder oss til å motta {nounPoss}.',
    brandFallback: 'merket',
    close: 'Lukk',
    accept: 'Godta og fortsett',
  },

  shipping: {
    title: 'Hvordan vil du sende det inn?',
    subtitle: 'Pakk {count} vare — én etikett for alt.',
    subtitlePlural: 'Pakk {count} varer — én etikett for alt.',
    loading: 'Henter fraktalternativer…',
    none: 'Det finnes ennå ingen fraktløsning for adressen din.',
    maxExceeded: 'Dette innbyttet overstiger maksverdien. Fjern en vare, og prøv igjen.',
    createFailed: 'Vi kunne ikke opprette innbyttet ditt. Prøv igjen.',
    desc_gls: 'Lever inn i en GLS-pakkeshop. Gratis, utskrevet etikett.',
    desc_dhl: 'Lever inn i en DHL-pakkeshop. Gratis, utskrevet etikett.',
    desc_dao: 'Et bud henter hos deg.',
    desc_shipmondo: 'Lever inn med en utskrevet etikett.',
    desc_pickup: 'Lever inn i den fysiske butikken vår.',
    desc_postnord: 'Lever inn hos et hvilket som helst PostNord-utleveringssted.',
  },

  story: {
    title: 'Fortell oss historien din',
    subtitle: 'Valgfritt — vi elsker å høre om plaggene dine.',
    placeholder: 'Fortell om dette plagget — hvor du brukte det, hvorfor du var glad i det…',
    consent:
      'Dere kan dele historien min (anonymt) for å inspirere andre kunder til å gi sine {noun} nytt liv.',
  },

  confirm: {
    topbarTitle: 'Bekreft',
    heroTitle: 'Da er du klar',
    heroBody: {
      received: 'Innbyttet ditt er registrert. Følg de tre trinnene nedenfor, så er {rewardPoss} på vei.',
      handin:
        'Innbyttet ditt er registrert. Følg de tre trinnene nedenfor — {rewardPoss} utstedes så snart du leverer inn pakken din.',
      instant:
        'Innbyttet ditt er registrert, og {rewardPoss} er på vei. Bare følg de tre trinnene nedenfor for å sende varene dine til oss.',
    },
    packTitle: 'Pakk {count} vare',
    packTitlePlural: 'Pakk {count} varer',
    packBody: 'Legg alt i én pakke. Ingen spesiell emballasje er nødvendig — gjenbruk en eske eller pose.',
    pickupPackBody: 'Pakk varene dine og ta dem med til den fysiske butikken vår — ingen fraktetikett er nødvendig.',
    shippingCode: 'Fraktkode',
    generatingLabel: 'Lager fraktetiketten din…',
    labelFailed: 'Vi kunne ikke lage etiketten din. Kontakt kundeservice.',
    showQr: 'Vis QR-kode',
    showLabel: 'Vis fraktetikett',
    dropTitle: 'Lever det inn',
    dropBody: 'Vis koden eller etiketten på fraktselskapets innleveringssted. ',
    findShop: 'Finn nærmeste innleveringssted →',
    pickupTitle: 'Lever inn i butikk',
    pickupBody: 'Ta med varene dine til butikken vår, så ordner vi resten.',
    rewardTitle: 'Få {rewardPoss}',
    // Delt rundt beløpet, som ConfirmPage viser i <b> mellom halvdelene («…sender vi deg [500 kr]
    // {reward} på e-post»). Som på dansk ligger bestemmelsen «ditt» i denne halvdelen og følger derfor
    // IKKE merkets ord: en butikk med et hankjønns-/hunkjønnsord («din kupong») må overstyre disse tre
    // linjene også. Intetkjønnsord (verdibevis, gavekort) berøres ikke.
    rewardBodyPre: {
      received: 'Når vi mottar pakken din, sender vi ditt ',
      handin: 'Så snart pakken din skannes ved innlevering, sender vi ditt ',
      instant: 'Ditt ',
    },
    rewardBodyPost: {
      received: ' {reward} på e-post innen 2 virkedager.',
      handin: ' {reward} på e-post. Gi det gjerne et par timer før sporingsdataene fra innleveringen når oss.',
      instant: ' {reward} er allerede på vei til innboksen din — bare send varene dine til oss via trinnene ovenfor.',
    },
    // INSTANT-strategi: verdibeviskoden vises her så snart Core har utstedt den.
    voucherReadyTitle: '{rewardPoss|cap} er klart',
    voucherIssuing: 'Oppretter {rewardPoss}…',
    voucherExpires: 'utløper {date}',
    copyCode: 'Kopier {reward}kode',
    codeCopied: '{reward|cap}kode kopiert',
    startAnother: 'Start et nytt innbytte',
    backToShop: 'Tilbake til butikken',
  },

  dropoff: {
    title: 'Nærmeste innleveringssteder',
    qrSupported: 'Støtter QR-innlevering — ingen utskrevet etikett er nødvendig.',
    qrNotSupported: 'Krever en utskrevet etikett.',
  },
}
